import { type I18nOptions } from 'vue-i18n';

import { useCookies } from '@bv/composables';
import { I18nConst, LANGUAGES } from '@bv/constants/i18n';
import { localStorageConsts } from '@bv/constants/localStorage';
import messages from '@bv/i18n/locale';
import { LanguageConfig, LanguageCode } from '@bv/types/plugins/i18n';

/**
 * The translation key for the application title.
 * @type {string}
 */
export const APP_TITLE_TRANS: string = I18nConst.appTitle;

/**
 * The default language code for the application.
 * @type {LanguageCode}
 */
export const DEFAULT_LOCALE: LanguageCode = LanguageCode.EN;

/**
 * The fallback language code to use when a translation is not available in the current locale.
 * @type {LanguageCode}
 */
export const FALLBACK_LOCALE: LanguageCode = LanguageCode.EN;

/**
 * Retrieves the initial language from storage. This function should be called
 * only after the Vue app instance is created.
 * @returns {LanguageConfig} The determined language configuration.
 */
export const getInitialLanguage = (): LanguageConfig => {
  const { get } = useCookies();
  const storedLang: LanguageConfig | undefined = getLangByCode(
    get<string>(localStorageConsts.language) as LanguageCode,
  );
  return storedLang || (getLangByCode(DEFAULT_LOCALE) as LanguageConfig);
};

/**
 * English plural category index for pipe-delimited locale messages ({@link vue-i18n} `pluralRules`).
 * Indices: zero, singular, many (more than 1).
 */
const englishPluralRules = (choice: number, choicesLength: number): number => {
  let result: number = 0;

  if (choice <= 0) {
    result = 0; // Zero
  } else if (choice === 1) {
    result = 1; // Singular
  } else {
    result = 2; // Many
  }

  return Math.min(result, choicesLength - 1);
};

/**
 * Arabic plural category index for pipe-delimited locale messages ({@link vue-i18n} `pluralRules`).
 * Indices: zero, singular, dual, few (3–10), many (more than 10).
 */
const arabicPluralRules = (choice: number, choicesLength: number): number => {
  let result: number = 0;

  if (choice <= 0) {
    result = 0; // Zero
  } else if (choice === 1) {
    result = 1; // Singular
  } else if (choice === 2) {
    result = 2; // Dual
  } else if (choice >= 3 && choice <= 10) {
    result = 3; // Few
  } else {
    result = 4; // Many
  }

  return Math.min(result, choicesLength - 1);
};

/**
 * Configuration options for the vue-i18n instance.
 * @type {I18nOptions}
 */
export const OPTIONS: I18nOptions = {
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: FALLBACK_LOCALE,
  messages,
  globalInjection: true,
  missingWarn: import.meta.env.VITE_ENV_NAME !== 'production',
  fallbackWarn: import.meta.env.VITE_ENV_NAME !== 'production',
  pluralRules: {
    [LanguageCode.EN]: englishPluralRules,
    [LanguageCode.AR]: arabicPluralRules,
  },
};

/**
 * Finds and returns a language configuration object by its language code.
 * @param {LanguageCode} [code] - The language code (e.g., 'en', 'ar') to search for.
 * @returns {LanguageConfig | undefined} The matching language configuration object, or undefined if not found.
 */
export function getLangByCode(code?: LanguageCode): LanguageConfig | undefined {
  return LANGUAGES.find((lang) => lang.code === code);
}

/**
 * Cycles to the next available language in the list.
 * If the current language is the last one, it wraps around to the first.
 * @param {LanguageCode} code - The current language code.
 * @returns {LanguageConfig} The next language configuration object in the cycle.
 */
export function nextLanguage(code: LanguageCode): LanguageConfig {
  const currentIndex: number = LANGUAGES.findIndex((lang) => lang.code === code);
  const nextIndex: number = currentIndex === LANGUAGES.length - 1 ? 0 : currentIndex + 1;
  return LANGUAGES.at(nextIndex)!;
}
